/**
 * Decodes day abbreviations into a human-readable format.
 * Handles various formats: concatenated days (MoWeFr), ranges (Mo-Fr), or special codes.
 * @param days - Day string from backend (e.g., "MoWeFr", "Mo-Fr", "TBA")
 * @returns Formatted day string (e.g., "Mo, We, Fr" or "TBA")
 */
export const decodeDays = (days: string | null | undefined): string => {
  const potentialDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Return empty string for null/undefined/empty input
  if (!days || days === '') {
    return '';
  }

  // Check if it's a special code like "TBA" (second char is 'B')
  if (days[1] === 'B') {
    return days;
  }

  // Handle day ranges (e.g., "Mo-Fr" becomes "MoTuWeTh Fr")
  if (days.includes('-')) {
    const [first, second] = days.split('-');
    const startIndex = potentialDays.indexOf(first);
    const endIndex = potentialDays.indexOf(second);
    
    // If invalid range, return as-is
    if (startIndex === -1 || endIndex === -1) return days;
    
    // Expand range to individual days
    let result = '';
    for (let i = startIndex; i <= endIndex; i++) {
      result += potentialDays[i];
    }
    days = result;
  }

  // Split concatenated days and join with commas (e.g., "MoWeFr" -> "Mo, We, Fr")
  return days.match(/[A-Z]*[^A-Z]+/g)?.join(', ') || days;
};

/**
 * Determines the appropriate icon for a building/location.
 * Returns special icons for online classes, off-campus locations, or TBA.
 * Otherwise returns a numbered map pin icon.
 * @param bldgCd - Building code (e.g., "WEB", "ONLN", "TBA")
 * @param pinNum - Pin number for on-campus buildings (0-based index)
 * @param isDarkMode - Whether the app is in dark mode (white pins in dark mode, black pins in light mode)
 * @returns Icon filename without extension
 */
export const getBuildingIcon = (bldgCd: string, pinNum: number, isDarkMode = true): string => {
  switch (bldgCd) {
    // Online classes
    case 'ONLN':
      return isDarkMode ? 'wifi' : 'wifi-black';
    case 'CANVAS':
      return isDarkMode ? 'canvas' : 'canvas-black';
    // Off-campus locations
    case 'SANDY':
    case 'ST. GEORGE':
    case 'BINGHAM HS':
    case 'BRIGHTON H':
    case 'COP HLS HS':
    case 'CORNER CYN':
    case 'COTTWD HS':
    case 'GRANGER HS':
    case 'HILLSIDE':
    case 'KEARNS HS':
    case 'MTN RDG HS':
    case 'MURRAY HS':
    case 'OLYMPUS HS':
    case 'PUBLIC SCH':
    case 'RIVERTON':
    case 'TYSVLLE HS':
    case 'W JRDN HS':
      return isDarkMode ? 'directions' : 'directions-black';
    // To be announced or empty
    case 'TBA':
    case '':
      return isDarkMode ? 'tba' : 'tba-black';
    // On-campus buildings get numbered pins (1-9)
    default:
      return `map-pin-${isDarkMode ? 'white' : 'black'}0${pinNum + 1}`;
  }
};

/**
 * Formats a Unix timestamp into a human-readable date string.
 * @param epochTime - Unix timestamp in milliseconds
 * @returns Formatted date string (M/D/YYYY)
 */
export const formatDate = (epochTime: number): string => {
  const date = new Date(epochTime);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Normalizes building codes to match the structures layer in ArcGIS.
 * Maps alternative building names/codes to their canonical abbreviations.
 * @param bldgCd - Building code from class schedule
 * @returns Normalized building code for ArcGIS query
 */
export const normalizeBuildingCode = (bldgCd: string): string => {
  // Mapping of alternative names to canonical building codes
  const mappings: { [key: string]: string } = {
    'BLDG 73': 'PTAB',
    'DUMKE': 'DGC',
    'FT D 638': 'FD 638',
    'HON CTR': 'FD 619',
    'HPEB': 'DHPEB',
    'HSEB': 'EHSEB',
    'M LI': 'M LIB',
    'NURS': 'SANDY',
    'S BEH AUD': 'S BEH',
    'SLC': 'GSESLC',
    'VOICE CTR': 'VOICE',
  };

  return mappings[bldgCd] || bldgCd;
};
